import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useBlocker, Location } from 'react-router';
import Modal from '../../shared/components/Modal';

interface Props {
  when: boolean;
  shouldBlockNavigation: () => boolean;
  onSave?: (locationPath: Location | null) => void;
  from?: Location;
  message?: string;
}

const RouteLeavingGuard: React.FC<Props> = (props: Props) => {
  const { when, shouldBlockNavigation, onSave, from, message } = props;
  const { t } = useTranslation();

  const blocker = useBlocker(
    ({ nextLocation }) =>
      when && shouldBlockNavigation() && (!from || from.pathname !== nextLocation.pathname)
  );

  useEffect(
    () => () => {
      // patch: scroll remain locked when redirect
      const body = document.querySelector('body');
      if (!body) {
        return;
      }
      body.classList.remove('modal-open');
    },
    [blocker.state]
  );

  const modalVisible = blocker.state === 'blocked';

  const handleCancelClick = () => {
    if (blocker.state === 'blocked') {
      blocker.reset();
    }
  };

  const handleConfirmNavigationClick = () => {
    if (blocker.state === 'blocked') {
      blocker.proceed();
    }
  };

  const actions = [
    { text: t('cancel'), action: handleCancelClick },
    { text: !onSave ? t('leave') : t('dontSave'), action: handleConfirmNavigationClick },
  ];
  if (onSave) {
    actions.push({
      text: t('saveChanges'),
      action: () => onSave(blocker.state === 'blocked' ? blocker.location : null),
    });
  }

  return (
    <Modal
      visible={modalVisible}
      setVisible={(next: React.SetStateAction<boolean>) => {
        if (next === false) {
          handleCancelClick();
        }
      }}
      actions={actions}
      title={t('saveChanges')}
    >
      {message || t('dirtyPopupMsg')}
    </Modal>
  );
};

export default RouteLeavingGuard;
